import React from 'react'
import { useTranslation } from 'react-i18next'

import { useSessionEntity } from '@context/EntityContext'
import { useLegalLinksContext } from '@context/LegalLinksContext'
import { BaseModalType } from '@context/ModalsContext'

import { Link } from '@ui/components/Link'
import { Modal } from '@ui/components/Modal'

import { TickmillCompaniesEnum } from '@utils/companyName.utils'
import { isOne, isZero } from '@utils/validations'

import { TickmillProductType } from '@model/TickmillProductType'

interface Props {
  selectedProductIds?: TickmillProductType[]
  risk: number | undefined
}

export const RiskWarningModal: BaseModalType<Props> = (props) => {
  const { close, risk, selectedProductIds } = props
  const { t } = useTranslation()
  const entity = useSessionEntity()
  const { docLinks } = useLegalLinksContext()

  const isProductTypeCFD =
    isOne(selectedProductIds?.length) && selectedProductIds?.includes(TickmillProductType.CFD)
  const isProductTypeETD =
    isOne(selectedProductIds?.length) && selectedProductIds?.includes(TickmillProductType.ETD)
  const isProductTypeCFDETD =
    isZero(selectedProductIds?.length) ||
    (selectedProductIds?.length === 2 &&
      selectedProductIds?.includes(TickmillProductType.CFD) &&
      selectedProductIds?.includes(TickmillProductType.ETD))

  const consolidatedEntities = [
    TickmillCompaniesEnum.TICKMILL_AS,
    TickmillCompaniesEnum.TICKMILL_SC,
    TickmillCompaniesEnum.TICKMILL_PA,
  ]

  const renderBody = () => {
    if (entity === TickmillCompaniesEnum.TICKMILL_EU) {
      return (
        <Modal.Body.Text>
          {t('Sign up.Risk Warning Desc EU', {
            risk,
          })}
        </Modal.Body.Text>
      )
    }

    if (entity === TickmillCompaniesEnum.TICKMILL_UK) {
      if (isProductTypeCFDETD) {
        return (
          <>
            <Modal.Body.Text>
              {t('highRiskWarning.CFD', {
                risk: risk,
                entity: 'Tickmill UK Ltd',
              })}{' '}
              <Link href={docLinks?.riskDisclosure}>{t('Footer.Risk Disclosure')}</Link>.
            </Modal.Body.Text>
            <Modal.Body.Text>
              {t('highRiskWarning.ETD', {
                risk: risk,
                entity: 'Tickmill UK Ltd',
              })}{' '}
              <Link href={docLinks?.riskDisclosure}>{t('Footer.Risk Disclosure')}</Link>{' '}
              {t('highRiskWarning.document')}.
            </Modal.Body.Text>
          </>
        )
      }
      if (isProductTypeETD && !isProductTypeCFD) {
        return (
          <Modal.Body.Text>
            <b>
              {t('Sign up.Risk Warning', {
                risk,
              })}
              :
            </b>{' '}
            {t('highRiskWarning.ETD', {
              risk,
            })}{' '}
            <Link href={docLinks?.riskDisclosure}>{t('Footer.Risk Disclosure')}</Link>{' '}
            {t('highRiskWarning.document')}.
          </Modal.Body.Text>
        )
      }
      if (isProductTypeCFD && !isProductTypeETD) {
        return (
          <Modal.Body.Text>
            {t('highRiskWarning.CFD', {
              risk: risk,
              entity: 'Tickmill UK Ltd',
            })}{' '}
            <Link href={docLinks?.riskDisclosure}>{t('Footer.Risk Disclosure')}</Link>.
          </Modal.Body.Text>
        )
      }
    }

    if (consolidatedEntities.includes(entity)) {
      return (
        <Modal.Body.Text>
          {risk
            ? t('Sign up.Risk Warning Desc Consolidated', { risk })
            : t('Sign up.Risk Warning Desc')}
        </Modal.Body.Text>
      )
    }

    return <Modal.Body.Text>{t('Sign up.Risk Warning Desc')}</Modal.Body.Text>
  }

  return (
    <Modal.Popup>
      <Modal.Header>{t('Sign up.Risk Warning')}</Modal.Header>
      <Modal.Body>{renderBody()}</Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Secondary onClick={close}>{t('Got It')}</Modal.Footer.Secondary>
      </Modal.Footer>
    </Modal.Popup>
  )
}
